import { Capacitor } from "@capacitor/core";
import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";

/**
 * A rendered free-trial length in the two grammatical forms the paywall needs.
 * Keeping both here means the copy never re-derives "1 week" → "1-week" inline.
 */
export interface TrialPeriodLabel {
  /** Noun phrase — "Try X for free" / "Get X to experience…" → "1 week", "7 days". */
  noun: string;
  /** Adjective phrase for the CTA — "Start X Free Trial" → "1-week", "7-day". */
  adjective: string;
}

type TrialUnit = "DAY" | "WEEK" | "MONTH" | "YEAR";

interface TrialDuration {
  value: number;
  unit: TrialUnit;
}

const UNIT_SINGULAR: Record<TrialUnit, string> = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
};

/**
 * Fallback used when the store trial length can't be read — the production
 * backend trial (not store-driven), web previews, or offerings that failed to
 * load. Both stores are configured with a 1-week (P1W) free trial, so a single
 * fallback covers every platform.
 */
const FALLBACK_DURATION: TrialDuration = { value: 1, unit: "WEEK" };

/** Accept only the four real units; "UNKNOWN" or anything unexpected → null. */
const normalizeUnit = (raw: string | undefined): TrialUnit | null =>
  raw === "DAY" || raw === "WEEK" || raw === "MONTH" || raw === "YEAR" ? raw : null;

const formatLabel = ({ value, unit }: TrialDuration): TrialPeriodLabel => {
  const singular = UNIT_SINGULAR[unit];
  return {
    noun: value === 1 ? `1 ${singular}` : `${value} ${singular}s`,
    adjective: `${value}-${singular}`,
  };
};

/**
 * Best-effort extraction of the free-trial length from the monthly package the
 * store surfaced. iOS carries it on the product's introductory offer; Android on
 * the subscription option's free pricing phase. The store is the source of truth
 * for copy — the purchase call is unaffected either way.
 */
const extractDuration = (monthlyPackage: PurchasesPackage): TrialDuration | null => {
  const platform = Capacitor.getPlatform();
  const product = monthlyPackage.product;

  if (platform === "ios") {
    const intro = product.introPrice;
    // Only a *free* introductory offer counts as a trial (a paid intro price isn't one).
    if (!intro || intro.price !== 0) {
      return null;
    }
    const unit = normalizeUnit(intro.periodUnit);
    if (!unit || intro.periodNumberOfUnits <= 0) {
      return null;
    }
    return { value: intro.periodNumberOfUnits, unit };
  }

  if (platform === "android") {
    // Google omits the free phase for ineligible accounts — same signal the
    // eligibility util reads (see trial-eligibility.ts).
    const freePhase =
      product.defaultOption?.freePhase ??
      (product.subscriptionOptions ?? []).find((option) => option.freePhase)?.freePhase ??
      null;
    if (!freePhase || freePhase.price.amountMicros !== 0) {
      return null;
    }
    const unit = normalizeUnit(freePhase.billingPeriod.unit);
    if (!unit || freePhase.billingPeriod.value <= 0) {
      return null;
    }
    return { value: freePhase.billingPeriod.value, unit };
  }

  return null;
};

/**
 * The real store trial length, or `null` when it can't be derived (web, no trial
 * phase, unrecognized unit). Prefer {@link getTrialPeriodLabelWithFallback} for
 * rendering — this is exposed mainly for tests / callers that want to branch on
 * "no store trial".
 */
export const getTrialPeriodLabel = (
  monthlyPackage: PurchasesPackage | undefined,
): TrialPeriodLabel | null => {
  if (!monthlyPackage) {
    return null;
  }
  const duration = extractDuration(monthlyPackage);
  return duration ? formatLabel(duration) : null;
};

/**
 * The store trial length if readable, else the platform-aware fallback — so the
 * paywall always has something correct to show. Callers stay one-liners and can't
 * drift on the fallback logic.
 */
export const getTrialPeriodLabelWithFallback = (
  monthlyPackage: PurchasesPackage | undefined,
): TrialPeriodLabel => getTrialPeriodLabel(monthlyPackage) ?? formatLabel(FALLBACK_DURATION);
